/**
 * Phase 54.8 (§2) — Multi-branch management dashboard.
 *
 * The former single-branch selector is gone. The Dashboard now renders
 * one KPI panel for ALL BRANCHES first, then one per individual branch,
 * in a stable "dashboard_order → name" ordering supplied by the server.
 *
 * Everything comes from ONE optimised backend call
 * (`GET /api/usm/analytics/overview-by-branch`) so the whole page is a
 * single Mongo aggregation round trip — no fan-out, no N requests per
 * branch, no client-side re-aggregation.
 *
 * Non-super-admin callers see just their own branch (backend enforced).
 * The premium visual language, card styling, animations and colour
 * palette are inherited from the original `KpiTile` component below.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import usmApi from "@/lib/usmApi";
import { useUsmAuth } from "@/contexts/UsmAuthContext";
import { PageHeader } from "./UsmLayout";
import { TrendingUp, ShoppingCart, Calendar, Award, ArrowUpRight, ArrowDownRight, Store } from "lucide-react";

const TRY = new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", minimumFractionDigits: 2 });

export default function UsmDashboard() {
  const { employee, branch, hasPerm } = useUsmAuth();
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!hasPerm("sales.view")) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    usmApi.get("/analytics/overview-by-branch")
      .then((r) => { if (alive) setSections(r.data?.sections || []); })
      .catch(() => { if (alive) setSections([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [hasPerm]);

  return (
    <div data-testid="usm-dashboard">
      <PageHeader
        title={`Hoş geldiniz, ${employee?.full_name?.split(" ")[0] || employee?.username}`}
      />

      <div className="mb-6 text-xs text-[#52525B]" data-testid="dashboard-scope-label">
        {branch ? `Şube: ${branch.name}` : "Süper yönetici · Tüm şubeler görüntüleniyor"}
      </div>

      {hasPerm("sales.view") ? (
        loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="border border-[#E7E5E4] p-4 bg-white animate-pulse h-[110px]" />
            ))}
          </div>
        ) : sections.length === 0 ? (
          <div className="text-sm text-[#71717A]">Henüz veri yok.</div>
        ) : (
          <div className="space-y-10" data-testid="dashboard-sections">
            {sections.map((s, idx) => <BranchPanel key={s.key} section={s} first={idx === 0} />)}
          </div>
        )
      ) : (
        <div className="text-sm text-[#71717A]">Satış verilerini görme yetkiniz bulunmuyor.</div>
      )}
    </div>
  );
}

function BranchPanel({ section, first }) {
  const isAll = section.is_aggregate;
  return (
    <section
      data-testid={`dashboard-section-${section.key}`}
      className={"transition-all duration-500 " + (first ? "animate-fade-in-up" : "")}
    >
      <header className="flex items-center gap-2 mb-3 pb-2 border-b border-[#E7E5E4]">
        {isAll ? (
          <span className="w-6 h-6 inline-flex items-center justify-center bg-[#0A0A0A] text-white text-[10px]">◆</span>
        ) : (
          <Store size={16} className="text-[#C8A96A]" strokeWidth={1.5} />
        )}
        <h2 className={"font-serif " + (isAll ? "text-lg" : "text-base") + " tracking-wide"}>
          {section.name}
        </h2>
        {isAll && (
          <span className="ml-2 text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Toplam</span>
        )}
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile
          testid={`kpi-${section.key}-today`} icon={ShoppingCart} label="Bugün"
          value={TRY.format(section.today?.amount ?? 0)}
          sub={`${section.today?.count ?? 0} işlem`}
          primary={isAll}
        />
        <KpiTile
          testid={`kpi-${section.key}-week`} icon={Calendar} label="Son 7 Gün"
          value={TRY.format(section.week?.amount ?? 0)}
          sub={`${section.week?.count ?? 0} işlem`}
          growth={section.week_growth_pct}
        />
        <KpiTile
          testid={`kpi-${section.key}-month`} icon={TrendingUp} label="Son 30 Gün"
          value={TRY.format(section.month?.amount ?? 0)}
          sub={`${section.month?.count ?? 0} işlem`}
          growth={section.month_growth_pct}
        />
        <KpiTile
          testid={`kpi-${section.key}-avg`} icon={Award} label="Ortalama Sepet"
          value={TRY.format(section.avg_ticket ?? 0)}
          sub={`En yük: ${TRY.format(section.highest_sale ?? 0)} · En düş: ${TRY.format(section.lowest_sale ?? 0)}`}
        />
      </div>
    </section>
  );
}

function KpiTile({ to, testid, icon: Icon, label, value, sub, primary, growth }) {
  const inner = (
    <>
      <div className="flex items-start justify-between mb-2">
        <p className={"text-[10px] uppercase tracking-[0.2em] " + (primary ? "text-white/70" : "text-[#71717A]")}>{label}</p>
        <Icon size={16} className="text-[#C8A96A]" strokeWidth={1.5} />
      </div>
      <p className={"font-finance text-xl " + (primary ? "" : "text-[#0A0A0A]")} data-testid={`${testid}-value`}>{value}</p>
      <div className="flex items-center justify-between mt-0.5">
        <p className={"text-[11px] " + (primary ? "text-white/60" : "text-[#71717A]")}>{sub}</p>
        {growth != null && (
          <span className={"text-[11px] inline-flex items-center gap-0.5 " + (growth >= 0 ? "text-emerald-500" : "text-red-500")}>
            {growth >= 0 ? <ArrowUpRight size={11} /> : <ArrowDownRight size={11} />}{Math.abs(growth)}%
          </span>
        )}
      </div>
    </>
  );
  const cls = "border p-4 transition-colors " + (primary
    ? "bg-[#0A0A0A] text-white border-[#0A0A0A] hover:bg-[#1c1917]"
    : "bg-white border-[#E7E5E4] hover:border-[#C8A96A]");
  return to
    ? <Link to={to} data-testid={testid} className={cls}>{inner}</Link>
    : <div data-testid={testid} className={cls}>{inner}</div>;
}
